/**
 * Entité Conversation - Représente une conversation avec l'agent RASK
 * Clean Architecture - Domain Layer
 * 
 * Pattern partagé avec raskmote-mobile/src/features/conversation/entities/Conversation.ts
 */

/**
 * @typedef {'idle' | 'processing' | 'error'} ConversationStatus
 */

/**
 * Classe Conversation
 * Gère l'état d'une conversation avec l'agent
 */
export class Conversation {
  /**
   * @param {string} [id] - ID de la conversation
   */
  constructor(id = null) {
    this.id = id || this.generateId();
    this.messages = [];
    this.status = 'idle';
    this.createdAt = new Date();
    this.updatedAt = new Date();
  }

  /**
   * Génère un ID unique pour la conversation (UUID v4 standard)
   * Format attendu par le backend : 550e8400-e29b-41d4-a716-446655440000
   * @returns {string}
   */
  generateId() {
    // Utiliser crypto.randomUUID() si disponible (navigateurs modernes Chrome 92+, Safari 15.4+)
    if (typeof window !== 'undefined' && 
        window.crypto && 
        typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    
    // Fallback : générer un UUID v4 RFC 4122 compliant
    // Utilise crypto.getRandomValues() si disponible (meilleure sécurité)
    if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
      const buffer = new Uint8Array(16);
      window.crypto.getRandomValues(buffer);
      
      // Définir les bits de version (4) et variant (RFC 4122)
      buffer[6] = ((buffer[6] & 0x0f) | 0x40); // Version 4
      buffer[8] = ((buffer[8] & 0x3f) | 0x80); // Variant RFC 4122
      
      // Convertir en format UUID
      const hex = Array.from(buffer)
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
      
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
    
    // Fallback ultime pour environnements très anciens (moins sécurisé)
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Ajoute un message à la conversation
   * @param {Object} message - Message à ajouter
   */
  addMessage(message) {
    this.messages.push(message);
    this.updatedAt = new Date();
  }

  /**
   * Met à jour le statut d'un message
   * @param {string} messageId - ID du message
   * @param {string} status - Nouveau statut
   */
  updateMessageStatus(messageId, status) {
    const message = this.messages.find(m => m.id === messageId);
    if (message) {
      message.status = status;
      this.updatedAt = new Date();
    }
  }

  /**
   * Met à jour le statut de la conversation
   * @param {ConversationStatus} status - Nouveau statut
   */
  updateStatus(status) {
    this.status = status;
    this.updatedAt = new Date();
  }

  /**
   * Récupère le dernier message utilisateur
   * @returns {Object|null}
   */
  getLastUserMessage() {
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (this.messages[i].type === 'user') {
        return this.messages[i];
      }
    }
    return null;
  }

  /**
   * Récupère le dernier message assistant
   * @returns {Object|null}
   */
  getLastAssistantMessage() {
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (this.messages[i].type === 'assistant') {
        return this.messages[i];
      }
    }
    return null;
  }

  /**
   * Récupère tous les messages
   * @returns {Array}
   */
  getMessages() {
    return [...this.messages];
  }

  /**
   * Efface la conversation
   */
  clear() {
    this.messages = [];
    this.status = 'idle';
    this.updatedAt = new Date();
  }

  /**
   * Récupère les statistiques de la conversation
   * @returns {Object}
   */
  getStats() {
    const userMessages = this.messages.filter(m => m.type === 'user').length;
    const assistantMessages = this.messages.filter(m => m.type === 'assistant').length;
    const errorMessages = this.messages.filter(m => m.error).length;

    return {
      messageCount: this.messages.length,
      userMessages,
      assistantMessages,
      errorMessages,
    };
  }

  /**
   * Sérialise la conversation pour le stockage
   * @returns {Object}
   */
  toJSON() {
    return {
      id: this.id,
      messages: this.messages,
      status: this.status,
      createdAt: this.createdAt.toISOString(),
      updatedAt: this.updatedAt.toISOString(),
    };
  }

  /**
   * Crée une conversation depuis un objet JSON
   * @param {Object} data - Données JSON
   * @returns {Conversation}
   */
  static fromJSON(data) {
    const conversation = new Conversation(data.id);
    conversation.messages = data.messages || [];
    conversation.status = data.status || 'idle';
    conversation.createdAt = new Date(data.createdAt);
    conversation.updatedAt = new Date(data.updatedAt);
    return conversation;
  }
}

export default Conversation;




